/**
 * 비밀번호 변경 페이지 (V37)
 *
 * 최초 로그인 시 비밀번호 변경을 강제하는 페이지입니다.
 * - 초기 비밀번호('ngle1234')와 다른 비밀번호 설정 필요
 * - 최소 8자 이상
 * - 변경 완료 후 메인 페이지로 이동
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { Key, Eye, EyeOff, CheckCircle, Loader, AlertCircle } from 'lucide-react';
import { functions } from '../../firebase/config';

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // V277: 한글 제거 필터
  const KOREAN_REGEX = /[ㄱ-ㅎㅏ-ㅣ가-힣]/g;
  const filterKorean = (value: string) => value.replace(KOREAN_REGEX, '');

  // 비밀번호 유효성 검사
  const validatePassword = (): string | null => {
    // V268: 공란/공백만 입력 차단
    if (!newPassword || newPassword.trim().length === 0) {
      return '비밀번호를 입력해주세요.';
    }
    // 공백 포함 여부 검사 (앞뒤 공백 및 중간 공백 모두 불허)
    if (/\s/.test(newPassword)) {
      return '비밀번호에 공백을 포함할 수 없습니다.';
    }
    // V277: 한글 포함 차단
    if (KOREAN_REGEX.test(newPassword)) {
      return '비밀번호에는 한글을 사용할 수 없습니다.';
    }
    if (newPassword.length < 8) {
      return '비밀번호는 최소 8자 이상이어야 합니다.';
    }
    if (newPassword === 'ngle1234') {
      return '초기 비밀번호와 다른 비밀번호를 사용해주세요.';
    }
    if (newPassword !== confirmPassword) {
      return '비밀번호가 일치하지 않습니다.';
    }
    return null;
  };

  // 공백 없는 비밀번호만 유효 (V268: trim 기준 공란 차단 추가)
  const hasNoWhitespace = !/\s/.test(newPassword);
  const hasNoKorean = !KOREAN_REGEX.test(newPassword); // V277
  const isValid = newPassword.trim().length > 0 &&
                  hasNoWhitespace &&
                  hasNoKorean &&
                  newPassword.length >= 8 &&
                  newPassword !== 'ngle1234' &&
                  newPassword === confirmPassword;

  // 비밀번호 변경 처리
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validatePassword();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      if (!functions) {
        // 데모 모드
        navigate('/announcements');
        return;
      }

      const changeMyPassword = httpsCallable<{ newPassword: string }, { success: boolean }>(
        functions,
        'changeMyPassword'
      );
      await changeMyPassword({ newPassword });

      // 메인 페이지로 이동
      navigate('/announcements');
    } catch (err: any) {
      console.error('비밀번호 변경 실패:', err);
      setError(err.message || '비밀번호 변경에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {/* 헤더 */}
        <div style={headerStyle}>
          <Key size={32} color="#3b82f6" />
          <h1 style={titleStyle}>비밀번호 변경</h1>
        </div>

        {/* 안내 메시지 */}
        <div style={infoBoxStyle}>
          <AlertCircle size={20} color="#f59e0b" />
          <p style={infoTextStyle}>
            보안을 위해 초기 비밀번호를 변경해야 합니다.<br />
            새로운 비밀번호를 설정해주세요.
          </p>
        </div>

        {/* 폼 */}
        <form onSubmit={handleSubmit}>
          {/* 새 비밀번호 */}
          <div style={fieldContainerStyle}>
            <label style={labelStyle}>새 비밀번호</label>
            <div style={inputWrapperStyle}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(filterKorean(e.target.value))}
                placeholder="8자 이상 입력"
                style={inputStyle}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={eyeButtonStyle}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {/* 비밀번호 강도 표시 */}
            <div style={strengthContainerStyle}>
              <div
                style={{
                  ...strengthBarStyle,
                  width: newPassword.length >= 8 ? '100%' : `${(newPassword.length / 8) * 100}%`,
                  backgroundColor: newPassword.length >= 8 ? '#10b981' : '#f59e0b',
                }}
              />
            </div>
            <p style={{
              ...hintStyle,
              color: /\s/.test(newPassword) ? '#ef4444' : '#6b7280',
            }}>
              {/\s/.test(newPassword) && '공백을 포함할 수 없습니다'}
              {!/\s/.test(newPassword) && newPassword.length > 0 && newPassword.length < 8 && `${8 - newPassword.length}자 더 입력해주세요`}
              {!/\s/.test(newPassword) && newPassword.length >= 8 && newPassword !== 'ngle1234' && '사용 가능한 비밀번호입니다'}
              {!/\s/.test(newPassword) && newPassword === 'ngle1234' && '초기 비밀번호와 동일합니다'}
            </p>
          </div>

          {/* 비밀번호 확인 */}
          <div style={fieldContainerStyle}>
            <label style={labelStyle}>비밀번호 확인</label>
            <div style={inputWrapperStyle}>
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(filterKorean(e.target.value))}
                placeholder="비밀번호 재입력"
                style={inputStyle}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                style={eyeButtonStyle}
              >
                {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {confirmPassword.length > 0 && (
              <p style={{
                ...hintStyle,
                color: newPassword === confirmPassword ? '#10b981' : '#ef4444',
              }}>
                {newPassword === confirmPassword ? '비밀번호가 일치합니다' : '비밀번호가 일치하지 않습니다'}
              </p>
            )}
          </div>

          {/* 에러 메시지 */}
          {error && <p style={errorStyle}>{error}</p>}

          {/* 제출 버튼 */}
          <button
            type="submit"
            style={isValid ? submitButtonStyle : disabledButtonStyle}
            disabled={!isValid || submitting}
          >
            {submitting ? (
              <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <CheckCircle size={20} />
            )}
            비밀번호 변경
          </button>
        </form>
      </div>
    </div>
  );
}

// 스타일
const containerStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#f3f4f6',
  padding: '20px',
};

const cardStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  padding: '32px',
  maxWidth: '420px',
  width: '100%',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '24px',
};

const titleStyle: React.CSSProperties = {
  fontSize: '24px',
  fontWeight: '700',
  margin: 0,
  color: '#1f2937',
};

const infoBoxStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  padding: '16px',
  backgroundColor: '#fffbeb',
  borderRadius: '8px',
  marginBottom: '24px',
};

const infoTextStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '14px',
  lineHeight: '1.5',
  color: '#92400e',
};

const fieldContainerStyle: React.CSSProperties = {
  marginBottom: '20px',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '14px',
  fontWeight: '500',
  color: '#374151',
  marginBottom: '6px',
};

const inputWrapperStyle: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 44px 12px 14px',
  fontSize: '15px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  outline: 'none',
};

const eyeButtonStyle: React.CSSProperties = {
  position: 'absolute',
  right: '12px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: '#9ca3af',
  padding: '4px',
};

const strengthContainerStyle: React.CSSProperties = {
  height: '4px',
  backgroundColor: '#e5e7eb',
  borderRadius: '2px',
  marginTop: '8px',
  overflow: 'hidden',
};

const strengthBarStyle: React.CSSProperties = {
  height: '100%',
  borderRadius: '2px',
  transition: 'width 0.3s ease, background-color 0.3s ease',
};

const hintStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#6b7280',
  marginTop: '6px',
};

const errorStyle: React.CSSProperties = {
  color: '#dc2626',
  fontSize: '14px',
  marginBottom: '16px',
};

const submitButtonStyle: React.CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '14px',
  fontSize: '16px',
  fontWeight: '600',
  color: 'white',
  backgroundColor: '#3b82f6',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
};

const disabledButtonStyle: React.CSSProperties = {
  ...submitButtonStyle,
  backgroundColor: '#9ca3af',
  cursor: 'not-allowed',
};
